import React, { useState, useEffect } from "react";
import axios from "axios";
import {
    Card,
    CardBody,
    CardHeader,
    Container,
    Row,
    Col,
    Button,
    Label,
    Alert,
    Badge,
    Nav,
    NavItem,
    NavLink,
    Table,
    FormGroup,
} from "reactstrap";
import Breadcrumbs from "../../../Components/Common/Breadcrumb";
import Select from "react-select";
import Dropzone from "react-dropzone";
import classnames from "classnames";

// Parse CSV text to array of objects
const parseCSV = (text: string): Record<string, string>[] => {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length < 2) return [];
    const headers = lines[0].split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/).map((h) => h.replace(/^"|"$/g, "").trim());
    const rows: Record<string, string>[] = [];
    for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/).map((v) => v.replace(/^"|"$/g, "").trim());
        const row: Record<string, string> = {};
        headers.forEach((h, idx) => {
            row[h] = values[idx] ?? "";
        });
        rows.push(row);
    }
    return rows;
};

// Parse Excel using xlsx (dynamic import for optional dependency)
const parseExcel = async (file: File): Promise<Record<string, string>[]> => {
    try {
        const XLSX = await import("xlsx");
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = e.target?.result;
                    if (!data) {
                        reject(new Error("Failed to read file"));
                        return;
                    }
                    const workbook = XLSX.read(data, { type: "binary" });
                    const sheetName = workbook.SheetNames[0];
                    const sheet = workbook.Sheets[sheetName];
                    const json = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as (string | number)[][];
                    if (json.length < 2) {
                        resolve([]);
                        return;
                    }
                    const headers = (json[0] as (string | number)[]).map((h) => String(h ?? "").trim());
                    const rows: Record<string, string>[] = [];
                    for (let i = 1; i < json.length; i++) {
                        const values = json[i] as (string | number)[];
                        const row: Record<string, string> = {};
                        headers.forEach((h, idx) => {
                            row[h] = String(values?.[idx] ?? "").trim();
                        });
                        rows.push(row);
                    }
                    resolve(rows);
                } catch (err) {
                    reject(err);
                }
            };
            reader.readAsBinaryString(file);
        });
    } catch (err) {
        throw new Error("Excel parsing requires the 'xlsx' package. Please run: npm install xlsx --legacy-peer-deps");
    }
};

const Marketing = () => {
    document.title = "Marketing | BizServe Admin";

    const [step, setStep] = useState(1);
    const [alertMsgg, setAlertMsgg] = useState<{ open: boolean; type: string; content: string }>({
        open: false,
        type: "success",
        content: "",
    });

    // Step 1: File upload
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [parsedData, setParsedData] = useState<Record<string, string>[]>([]);
    const [columns, setColumns] = useState<string[]>([]);
    const [loadingParse, setLoadingParse] = useState(false);

    // Step 2: Column mapping
    const [companyColumn, setCompanyColumn] = useState<string | null>(null);
    const [emailColumn, setEmailColumn] = useState<string | null>(null);
    const [nameColumn, setNameColumn] = useState<string | null>(null);

    // Step 3: Domain, partner & plans
    const [domains, setDomains] = useState<any[]>([]);
    const [selectedDomain, setSelectedDomain] = useState<any>(null);
    const [partners, setPartners] = useState<any[]>([]);
    const [selectedPartner, setSelectedPartner] = useState<any>(null);
    const [plans, setPlans] = useState<any[]>([]);
    const [selectedPlans, setSelectedPlans] = useState<any[]>([]);
    const [categories, setCategories] = useState<any[]>([]);
    const [loadingDomains, setLoadingDomains] = useState(false);
    const [loadingPlans, setLoadingPlans] = useState(false);

    // Step 4: Generated email
    const [generatedHtml, setGeneratedHtml] = useState<string>("");
    const [sending, setSending] = useState(false);


    const handleAlertClose = () => {
        setAlertMsgg((p) => ({ ...p, open: false }));
    };

    const handleFileDrop = async (acceptedFiles: File[]) => {
        const file = acceptedFiles[0];
        if (!file) return;
        setUploadedFile(file);
        setLoadingParse(true);
        setParsedData([]);
        setColumns([]);
        setCompanyColumn(null);
        setEmailColumn(null);
        setNameColumn(null);
        try {
            const ext = file.name.split(".").pop()?.toLowerCase();
            let data: Record<string, string>[];
            if (ext === "csv") {
                const text = await file.text();
                data = parseCSV(text);
            } else if (ext === "xlsx" || ext === "xls") {
                data = await parseExcel(file);
            } else {
                setAlertMsgg({ open: true, type: "error", content: "Please upload a .csv or .xlsx file." });
                setLoadingParse(false);
                return;
            }
            setParsedData(data);
            const cols = data.length > 0 ? Object.keys(data[0]) : [];
            setColumns(cols);
            if (cols.length > 0) {
                // Auto-detect email column
                const emailLike = cols.find((c) => /email|e-mail|e_mail/i.test(c)) ?? cols[0];
                setEmailColumn(emailLike);
                const companyLike = cols.find((c) => /company|organization|business/i.test(c)) ?? cols[0];
                setCompanyColumn(companyLike);
                const nameLike = cols.find((c) => /name|contact|recipient|first.?name/i.test(c));
                setNameColumn(nameLike ?? null);
            }
            setAlertMsgg({ open: true, type: "success", content: `Parsed ${data.length} rows. Select columns and proceed.` });
        } catch (err: any) {
            setAlertMsgg({ open: true, type: "error", content: err?.message ?? "Failed to parse file." });
        }
        setLoadingParse(false);
    };

    const fetchDomains = async () => {
        setLoadingDomains(true);
        try {
            const response = await axios.post("accounts/handleadmdomains", { render: true }, { withCredentials: true });
            if (response.status === "SENT" && Array.isArray(response.domains)) {
                setDomains(response.domains);
                if (response.domains.length > 0 && !selectedDomain) {
                    setSelectedDomain(response.domains[0]);
                }
            }
        } catch (err) {
            setAlertMsgg({ open: true, type: "error", content: "Failed to load domains." });
        }
        setLoadingDomains(false);
    };

    const fetchPlans = async () => {
        if (!selectedDomain?.id) return;
        setLoadingPlans(true);
        const partnerParam = selectedPartner?.id != null ? selectedPartner.id : "all";
        try {
            const [productsRes, categoriesRes, partnersRes] = await Promise.all([
                axios.get("/payments/listDomainProducts", {
                    params: { domain: selectedDomain.id, category: "all", partner: partnerParam },
                    withCredentials: true,
                }),
                axios.get("/payments/listDomainCategories", {
                    params: { domain: selectedDomain.id },
                    withCredentials: true,
                }),
                axios.get("/payments/listDomainPartners", {
                    params: { domain: selectedDomain.id },
                    withCredentials: true,
                }),
            ]);
            if (productsRes?.status === "SENT") {
                const plansList = (productsRes.Plan || []).map((p: any) => ({ ...p, _productType: "Plan" }));
                const oneTimeList = (productsRes.OneTimeProduct || []).map((p: any) => ({ ...p, _productType: "OneTimeProduct" }));
                setPlans([...plansList, ...oneTimeList]);
                setSelectedPlans([]);
            }
            if (categoriesRes?.status === "SENT") setCategories(categoriesRes.categories || []);
            if (partnersRes?.status === "SENT") setPartners(partnersRes.partners || []);
        } catch (err) {
            setAlertMsgg({ open: true, type: "error", content: "Failed to load plans." });
        }
        setLoadingPlans(false);
    };

    const getCategoryLabels = (plan: any): string[] => {
        const cats = plan?.categories;
        if (!Array.isArray(cats)) return [];
        return cats
            .map((c: any) => {
                if (typeof c === "object" && c?.name) return String(c.name);
                const id = Number(c);
                if (!Number.isFinite(id)) return "";
                const found = categories.find((x: any) => Number(x?.id) === id);
                return found?.name ? String(found.name) : "";
            })
            .filter((s: string) => !!s);
    };

    const getPartnerLabels = (plan: any): string[] => {
        const ps = plan?.partners;
        if (!Array.isArray(ps)) return [];
        return ps
            .map((p: any) => {
                if (typeof p === "object") {
                    const company = p?.company_name ?? p?.companyname;
                    const first = p?.first_name ?? p?.firstname ?? "";
                    const last = p?.last_name ?? p?.lastname ?? "";
                    if (company) return String(company);
                    if (first || last) return `${first} ${last}`.trim();
                    if (p?.name) return String(p.name);
                }
                const idRaw = p?.partner_id ?? p?.id ?? p;
                if (idRaw == null || idRaw === "") return "";
                const id = String(idRaw);
                const found = partners.find((x: any) => String(x?.id) === id);
                if (!found) return "";
                const company = found?.company_name ?? found?.companyname;
                const first = found?.first_name ?? found?.firstname ?? "";
                const last = found?.last_name ?? found?.lastname ?? "";
                if (company) return String(company);
                if (first || last) return `${first} ${last}`.trim();
                return found?.name ? String(found.name) : "";
            })
            .filter((s: string) => !!s);
    };

    const getPartnerDisplayForPlan = (plan: any): string => {
        const labels = getPartnerLabels(plan);
        return labels.length > 0 ? labels[0] : "our expert partners";
    };

    const getPartnerDisplayWithAddress = (plan: any): string => {
        const ps = plan?.partners;
        if (!Array.isArray(ps) || ps.length === 0) return "our expert partners";
        const p = ps[0];
        const idRaw = typeof p === "object" ? (p?.id ?? p?.partner_id) : p;
        const id = idRaw != null && idRaw !== "" ? String(idRaw) : "";
        const found = id ? partners.find((x: any) => String(x?.id) === id) : undefined;
        const displayName = toTitleCase((found?.company_name?.trim() || getPartnerDisplayForPlan(plan)) || "our expert partners");
        const city = found?.city?.trim() || "";
        const state = found?.state?.trim() || "";
        const zip = found?.zipcode?.trim() || "";
        const hasAddress = !!(city || state || zip);
        if (!hasAddress) return displayName;
        const place = toTitleCase([city, state].filter(Boolean).join(" "));
        const parts = [displayName, place, "USA"];
        if (zip) parts.push(zip);
        return parts.join(", ");
    };

    useEffect(() => {
        fetchDomains();
    }, []);

    useEffect(() => {
        if (selectedDomain?.id) fetchPlans();
        else {
            setPlans([]);
            setPartners([]);
            setSelectedPartner(null);
        }
    }, [selectedDomain?.id, selectedPartner?.id]);

    const canProceedStep1 = parsedData.length > 0 && columns.length > 0;
    const canProceedStep2 = companyColumn && emailColumn;
    const canProceedStep3 = selectedDomain && selectedPlans.length > 0;

    const selectPlan = (plan: any) => {
        setSelectedPlans((prev) => {
            const match = (p: any) => p.id === plan.id && (p._productType || "Plan") === (plan._productType || "Plan");
            if (prev.some(match)) return [];
            return [plan];
        });
    };

    const toTitleCase = (s: string): string => {
        if (s == null || typeof s !== "string") return "";
        const trimmed = String(s).trim();
        if (!trimmed) return "";
        return trimmed
            .toLowerCase()
            .split(/\s+/)
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(" ");
    };

    const safeStr = (val: unknown, fallback: string = ""): string => {
        if (val == null) return fallback;
        const s = String(val).trim();
        return s || fallback;
    };

    /** Partner dropdown label: "First Name Last Name (Company name)" or fallbacks, in title case */
    const getPartnerDropdownLabel = (p: any): string => {
        if (!p) return "";
        const rawName = safeStr(p.name || [p.first_name, p.last_name].filter(Boolean).join(" "), "");
        const rawCompany = safeStr(p.company_name ?? p.companyname, "");
        const name = toTitleCase(rawName);
        const company = toTitleCase(rawCompany);
        if (name && company) return `${name} (${company})`;
        if (company) return company;
        if (name) return name;
        return `Partner #${p.id}`;
    };

    const escapeHtml = (s: string): string => {
        return String(s)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");
    };

    /** Converts markdown bold/italic to HTML, preserves newlines as <br>, and escapes the rest for safe use in email. */
    const markdownToHtml = (s: string): string => {
        const escaped = escapeHtml(s);
        let out = escaped.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
        out = out.replace(/__(.*?)__/g, "<strong>$1</strong>");
        out = out.replace(/\*([^*]+?)\*/g, "<em>$1</em>");
        out = out.replace(/_([^_]+?)_/g, "<em>$1</em>");
        // Preserve all line breaks from product descriptions in the email body
        out = out.replace(/\r\n/g, "\n").replace(/\n/g, "<br>");
        return out;
    };

    /** Paragraph-aware version for longer descriptions: splits on blank lines, keeps single newlines as <br>, and controls spacing via margin/line-height. */
    const markdownToHtmlParagraphs = (s: string): string => {
        const normalized = s.replace(/\r\n/g, "\n");
        const rawParagraphs = normalized.split(/\n{2,}/);
        const paragraphs = rawParagraphs.map((p) => p.trim()).filter(Boolean);
        if (paragraphs.length === 0) return "";
        return paragraphs
            .map(
                (p) =>
                    `<p style="margin:0 0 6px;line-height:1.3;">${markdownToHtml(p)}</p>`
            )
            .join("");
    };

    const formatPrice = (plan: any): string => {
        const p = plan?.price;
        const pricingType = plan?.pricing_type || "fixed";
        if (pricingType === "contact") return "Contact us";
        if (pricingType === "external") return plan?.external_url ? "View details" : "Contact us";
        if (p == null) return "Contact us";
        const num = Number(p);
        return isNaN(num) ? String(p) : "$" + num.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    };

    const generateEmailHtml = () => {
        const plan = selectedPlans[0];
        if (!plan) return "";
        const isOneTime = plan._productType === "OneTimeProduct";
        const itemLabel = isOneTime ? "product" : "plan";
        const domainName = toTitleCase(safeStr(selectedDomain?.name, "BizServe"));
        const logoUrl = "https://portal.bizserveus.com/static/media/darklogo.eaa6fe1646d8b9b636e1.png";
        const partnerDisplay = toTitleCase(safeStr(getPartnerDisplayForPlan(plan), "our expert partners"));
        const partnerDisplayWithAddress = safeStr(getPartnerDisplayWithAddress(plan), "our expert partners");
        const planName = safeStr(plan?.title || plan?.name, isOneTime ? "Product" : "Plan");
        const planDesc = safeStr(plan?.description, "");
        const planPrice = safeStr(formatPrice(plan), "Contact us");
        const features = Array.isArray(plan?.features) ? plan.features.filter((f: unknown) => f != null) : [];
        const categoryLabels = getCategoryLabels(plan).map((c) => toTitleCase(safeStr(c, ""))).filter(Boolean);
        const categoryDisplay = safeStr(categoryLabels[0], "");
        const portalUrl = "https://portal.bizserveus.com";
        const signUpUrl = "https://portal.bizserveus.com/register";
        const featuresList = features.length > 0
            ? `<ul style="margin:16px 0 0;padding-left:20px;color:#5c5c5c;font-size:14px;line-height:1.6;">${features.map((f: unknown) => `<li>${markdownToHtml(safeStr(f, ""))}</li>`).join("")}</ul>`
            : "";

        return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>BizServe — ${escapeHtml(planName)}</title>
</head>
<body style="margin:0;padding:0;font-family:'Segoe UI',Tahoma,Geneva,Verdana,sans-serif;background-color:#ffffff;">
  <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color:#f8fafc;">
    <tr>
      <td align="center" style="padding:40px 20px;">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="600" style="max-width:600px;background:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 20px 60px rgba(14,165,233,0.25);">
          <!-- Header with Logo -->
          <tr>
            <td style="background:linear-gradient(135deg,#e0f2fe 0%,#7dd3fc 50%,#38bdf8 100%);padding:32px;text-align:center;">
              <img src="${logoUrl}" alt="BizServe" style="display:block;margin:0 auto 16px;max-width:120px;height:auto;" />
            </td>
          </tr>
          <!-- What is BizServe -->
          <tr>
            <td style="padding:28px 32px 16px;">
              <p style="margin:0;color:#5c5c5c;font-size:15px;line-height:1.6;">
                We&rsquo;re BizServe&mdash;the marketplace where businesses like {{company_name}} find trusted experts for what you need. We handpick our partners and their offerings so you get quality service without the guesswork. Think of us as your bridge to the right professionals.
              </p>
            </td>
          </tr>
          <!-- Partnership offer -->
          <tr>
            <td style="padding:0 32px 24px;">
              <p style="margin:0;color:#1a1a2e;font-size:16px;line-height:1.6;">
                BizServe is proudly offering <strong>${escapeHtml(planName)}</strong> in partnership with ${escapeHtml(partnerDisplayWithAddress)}.
              </p>
            </td>
          </tr>
          <!-- Plan details -->
          <tr>
            <td style="padding:0 32px 24px;">
              <div style="border:1px solid #e8e8e8;border-radius:12px;padding:24px;background:#f8f9fc;">
                <h3 style="margin:0 0 12px;color:#1a1a2e;font-size:17px;">${escapeHtml(planName)}</h3>
                ${
                    planDesc
                        ? `<div style="margin:0;color:#5c5c5c;font-size:14px;">${markdownToHtmlParagraphs(
                              planDesc
                          )}</div>`
                        : ""
                }
                ${featuresList}
                ${planPrice ? `<p style="margin:16px 0 0;font-size:20px;font-weight:700;color:#0284c7;">${escapeHtml(planPrice)}</p>` : ""}
              </div>
            </td>
          </tr>
          <!-- Instructions to buy -->
          <tr>
            <td style="padding:0 32px 28px;">
              <h3 style="margin:0 0 12px;color:#1a1a2e;font-size:17px;">How to Purchase This ${itemLabel === "plan" ? "Plan" : "Product"}</h3>
              <p style="margin:0 0 16px;color:#5c5c5c;font-size:14px;">Follow these simple steps to get started:</p>
              <ol style="margin:0;padding-left:20px;color:#5c5c5c;font-size:14px;line-height:2;">
                <li>Click <strong>Sign up</strong> to create your free account.</li>
                <li>Fill in your details and complete the registration.</li>
                <li>Once you&rsquo;re logged in, go to <strong>Home</strong> at <a href="${portalUrl}" style="color:#0284c7;font-weight:600;">portal.bizserveus.com</a>.</li>
                <li>In the menu on the left side, click on <strong>${escapeHtml(domainName)}</strong>.</li>
                <li>Then click <strong>Packages and Pricing</strong> to see available plans and products.</li>
                ${isOneTime ? "<li>Then click <strong>Products &amp; services</strong> to see available products.</li>" : ""}
                <li>In the <strong>top right corner</strong> of the page, you&rsquo;ll see filter dropdowns. Select <strong>${escapeHtml(partnerDisplay)}</strong> as the partner${categoryDisplay ? ` and <strong>${escapeHtml(categoryDisplay)}</strong> as the category` : ""} to find this ${itemLabel}.</li>
                <li>Find this ${itemLabel} and click the <strong>Buy Now</strong> button.</li>
              </ol>
              <p style="margin:16px 0 0;">
                <a href="${signUpUrl}" style="display:inline-block;background:linear-gradient(135deg,#2563eb 0%,#0ea5e9 100%);color:#ffffff!important;text-decoration:none;padding:12px 24px;border-radius:8px;font-weight:600;font-size:14px;box-shadow:0 4px 14px rgba(14,165,233,0.4);">Sign Up at BizServe</a>
              </p>
            </td>
          </tr>
          <!-- Professional conditions -->
          <tr>
            <td style="padding:20px 32px;background:linear-gradient(180deg,#0f172a 0%,#1e293b 100%);color:rgba(255,255,255,0.8);font-size:11px;line-height:1.5;">
              <p style="margin:0;">This email and the offer described herein are subject to BizServe's Terms of Service and Privacy Policy. Pricing and availability are subject to change. By completing a purchase, you agree to the applicable purchase agreement and professional service terms. BizServe acts as a marketplace platform; service delivery is facilitated by the partner. For support, visit portal.bizserveus.com or contact our team.</p>
              <p style="margin:12px 0 0;">&copy; ${new Date().getFullYear()} BizServe. All rights reserved.</p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
`.trim();
    };

    const handleGenerate = () => {
        const html = generateEmailHtml();
        setGeneratedHtml(html);
        setStep(4);
    };

    const handleSend = async () => {
        if (!emailColumn || !generatedHtml) return;
        setSending(true);
        try {
            const recipients = parsedData
                .map((row) => {
                    const email = (row[emailColumn] || "").trim();
                    if (!email || !email.includes("@")) return null;
                    return {
                        email,
                        recipient_name: nameColumn ? (row[nameColumn] || "").trim() : "",
                        company_name: companyColumn ? (row[companyColumn] || "").trim() : "",
                    };
                })
                .filter((r): r is { email: string; recipient_name: string; company_name: string } => !!r);
            if (recipients.length === 0) {
                setAlertMsgg({ open: true, type: "error", content: "No valid email addresses found." });
                setSending(false);
                return;
            }
            const planName = selectedPlans[0]?.title || selectedPlans[0]?.name || "Plan";
            const response = await axios.post(
                "/payments/sendMarketingEmail/",
                {
                    subject: `${planName} might interest you - BizServe`,
                    html_content: generatedHtml,
                    recipients,
                },
                { withCredentials: true }
            );
            const res = response?.data ?? response;
            if (res?.status === "SENT") {
                const { sent, failed, total } = res;
                const msg = failed.length > 0
                    ? `Sent ${sent} of ${total}. Failed: ${failed.slice(0, 3).join("; ")}${failed.length > 3 ? "..." : ""}`
                    : `Successfully sent to ${sent} recipients.`;
                setAlertMsgg({ open: true, type: sent > 0 ? "success" : "warning", content: msg });
            } else {
                setAlertMsgg({ open: true, type: "error", content: res?.error || "Failed to send." });
            }
        } catch (err: any) {
            const errMsg = err?.response?.data?.error ?? err?.message ?? "Failed to send emails.";
                setAlertMsgg({ open: true, type: "error", content: errMsg });
        }
        setSending(false);
    };

    const steps = [
        { id: 1, title: "Upload File", icon: "bx-upload" },
        { id: 2, title: "Map Columns", icon: "bx-table" },
        { id: 3, title: "Domain & Plans", icon: "bx-package" },
        { id: 4, title: "Email Preview", icon: "bx-envelope" },
    ];

    return (
        <React.Fragment>
            {sending && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 9999,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(0,0,0,0.5)",
                    }}
                >
                    <div
                        style={{
                            background: "#fff",
                            padding: "32px 48px",
                            borderRadius: 12,
                            boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 16,
                        }}
                    >
                        <div className="spinner-border text-primary" style={{ width: 48, height: 48 }} role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <span style={{ fontSize: 16, fontWeight: 500 }}>Sending emails...</span>
                    </div>
                </div>
            )}
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Admin" breadcrumbItem="Marketing" />
                    {alertMsgg.open && (
                        <Alert color={alertMsgg.type as any} toggle={handleAlertClose} className="mb-3">
                            {alertMsgg.content}
                        </Alert>
                    )}

                    <Nav pills className="nav-pills nav-primary mb-4">
                        {steps.map((s) => (
                            <NavItem key={s.id}>
                                <NavLink
                                    className={classnames({ active: step === s.id })}
                                    onClick={() => setStep(s.id)}
                                    style={{ cursor: "pointer" }}
                                >
                                    <i className={`bx ${s.icon} me-1`}></i>
                                    {s.title}
                                </NavLink>
                            </NavItem>
                        ))}
                    </Nav>

                    {step === 1 && (
                        <Card>
                            <CardHeader>
                                <h5 className="card-title mb-0">Step 1: Upload Excel or CSV</h5>
                            </CardHeader>
                            <CardBody>
                                <p className="text-muted mb-3">
                                    Upload a spreadsheet containing company names and email addresses. Supported formats: .csv, .xlsx, .xls
                                </p>
                                <Dropzone
                                    onDrop={handleFileDrop}
                                    accept={{
                                        "text/csv": [".csv"],
                                        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
                                        "application/vnd.ms-excel": [".xls"],
                                    }}
                                    maxFiles={1}
                                    disabled={loadingParse}
                                >
                                    {({ getRootProps, getInputProps }) => (
                                        <div
                                            {...getRootProps()}
                                            className="border border-2 border-dashed rounded p-5 text-center"
                                            style={{
                                                background: "var(--bs-light)",
                                                cursor: loadingParse ? "wait" : "pointer",
                                            }}
                                        >
                                            <input {...getInputProps()} />
                                            <i className="bx bx-cloud-upload display-4 text-muted"></i>
                                            <p className="mt-2 mb-0">
                                                {loadingParse ? "Parsing..." : "Drop file here or click to upload"}
                                            </p>
                                            {uploadedFile && (
                                                <Badge color="success" className="mt-2">
                                                    {uploadedFile.name} ({parsedData.length} rows)
                                                </Badge>
                                            )}
                                        </div>
                                    )}
                                </Dropzone>
                                {canProceedStep1 && (
                                    <Button color="primary" className="mt-3" onClick={() => setStep(2)}>
                                        Next: Map Columns <i className="bx bx-right-arrow-alt ms-1"></i>
                                    </Button>
                                )}
                            </CardBody>
                        </Card>
                    )}

                    {step === 2 && (
                        <Card>
                            <CardHeader>
                                <h5 className="card-title mb-0">Step 2: Select Company Name & Email Column</h5>
                            </CardHeader>
                            <CardBody>
                                <Row>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Company Name Column</Label>
                                            <Select
                                                value={companyColumn ? { label: companyColumn, value: companyColumn } : null}
                                                onChange={(e: any) => setCompanyColumn(e?.value ?? null)}
                                                options={columns.map((c) => ({ label: c, value: c }))}
                                                placeholder="Select column..."
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Email Column</Label>
                                            <Select
                                                value={emailColumn ? { label: emailColumn, value: emailColumn } : null}
                                                onChange={(e: any) => setEmailColumn(e?.value ?? null)}
                                                options={columns.map((c) => ({ label: c, value: c }))}
                                                placeholder="Select column..."
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Recipient Name Column <small className="text-muted">(optional, for personalization)</small></Label>
                                            <Select
                                                value={nameColumn ? { label: nameColumn, value: nameColumn } : { label: "(None)", value: "__none__" }}
                                                onChange={(e: any) => setNameColumn(e?.value && e.value !== "__none__" ? e.value : null)}
                                                options={[{ label: "(None)", value: "__none__" }, ...columns.map((c) => ({ label: c, value: c }))]}
                                                placeholder="Select column..."
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <div className="mb-3">
                                    <Label>Preview (first 5 rows)</Label>
                                    <Table size="sm" bordered>
                                        <thead>
                                            <tr>
                                                <th>{companyColumn || "Company"}</th>
                                                <th>{emailColumn || "Email"}</th>
                                                {nameColumn && <th>Recipient</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {parsedData.slice(0, 5).map((row, i) => (
                                                <tr key={i}>
                                                    <td>{companyColumn ? row[companyColumn] : "-"}</td>
                                                    <td>{emailColumn ? row[emailColumn] : "-"}</td>
                                                    {nameColumn && <td>{row[nameColumn] || "-"}</td>}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                                <Button color="secondary" className="me-2" onClick={() => setStep(1)}>
                                    Back
                                </Button>
                                <Button color="primary" onClick={() => setStep(3)} disabled={!canProceedStep2}>
                                    Next: Choose Domain & Plans <i className="bx bx-right-arrow-alt ms-1"></i>
                                </Button>
                            </CardBody>
                        </Card>
                    )}

                    {step === 3 && (
                        <Card>
                            <CardHeader>
                                <h5 className="card-title mb-0">Step 3: Choose Domain & Plans</h5>
                            </CardHeader>
                            <CardBody>
                                <Row>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Domain</Label>
                                            <Select
                                                value={selectedDomain ? { label: selectedDomain.name, value: selectedDomain.id } : null}
                                                onChange={(e: any) => {
                                                    const d = domains.find((x) => x.id === e?.value);
                                                    setSelectedDomain(d ?? null);
                                                    setSelectedPartner(null);
                                                }}
                                                options={domains.map((d) => ({ label: d.name, value: d.id }))}
                                                isLoading={loadingDomains}
                                                placeholder="Select domain..."
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Partner</Label>
                                            <Select
                                                value={
                                                    selectedPartner
                                                        ? {
                                                              label: getPartnerDropdownLabel(selectedPartner),
                                                              value: selectedPartner.id,
                                                          }
                                                        : { label: "All partners", value: "" }
                                                }
                                                onChange={(e: any) => {
                                                    if (e?.value === "" || e?.value == null) {
                                                        setSelectedPartner(null);
                                                        return;
                                                    }
                                                    const p = partners.find((x: any) => x.id === e?.value);
                                                    setSelectedPartner(p ?? null);
                                                }}
                                                options={[
                                                    { label: "All partners", value: "" },
                                                    ...partners.map((p: any) => ({
                                                        label: getPartnerDropdownLabel(p),
                                                        value: p.id,
                                                    })),
                                                ]}
                                                isDisabled={!selectedDomain?.id || partners.length === 0}
                                                placeholder={!selectedDomain?.id ? "Select domain first..." : "Select partner..."}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <FormGroup>
                                    <Label>Select Plan or Product (one only)</Label>
                                    {loadingPlans ? (
                                        <p className="text-muted">Loading plans and products...</p>
                                    ) : plans.length === 0 ? (
                                        <p className="text-muted">No plans or products found for this domain and partner.</p>
                                    ) : (
                                        <div className="d-flex flex-wrap gap-3">
                                            {plans.map((plan) => {
                                                const match = (p: any) => p.id === plan.id && (p._productType || "Plan") === (plan._productType || "Plan");
                                                const isSelected = selectedPlans.some(match);
                                                const itemKey = `${plan._productType || "Plan"}-${plan.id}`;
                                                return (
                                                    <div
                                                        key={itemKey}
                                                        role="button"
                                                        tabIndex={0}
                                                        onClick={() => selectPlan(plan)}
                                                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") selectPlan(plan); }}
                                                        className="rounded border p-3 text-start"
                                                        style={{
                                                            minWidth: 180,
                                                            cursor: "pointer",
                                                            borderWidth: "2px",
                                                            borderColor: isSelected ? "var(--bs-primary)" : "#dee2e6",
                                                            backgroundColor: isSelected ? "rgba(var(--bs-primary-rgb), 0.1)" : "#f8f9fa",
                                                            color: isSelected ? "var(--bs-primary)" : "#212529",
                                                        }}
                                                    >
                                                        <div className="d-flex align-items-center justify-content-between">
                                                            <span className="fw-semibold">{plan.title || plan.name}</span>
                                                            {isSelected && <i className="bx bx-check-circle text-primary fs-5"></i>}
                                                        </div>
                                                        <div className="mt-2 d-flex flex-wrap gap-1" style={{ fontSize: "0.8rem" }}>
                                                            {plan._productType === "OneTimeProduct" && (
                                                                <Badge color="warning" pill>One-time Product</Badge>
                                                            )}
                                                            {getCategoryLabels(plan).length > 0 && (
                                                                <Badge color="info" pill>{toTitleCase(getCategoryLabels(plan).join(", "))}</Badge>
                                                            )}
                                                            {getPartnerLabels(plan).length > 0 && (
                                                                <Badge color="secondary" pill>{toTitleCase(getPartnerLabels(plan).join(", "))}</Badge>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </FormGroup>
                                <div className="mt-3">
                                    <Button color="secondary" className="me-2" onClick={() => setStep(2)}>
                                        Back
                                    </Button>
                                    <Button color="primary" onClick={handleGenerate} disabled={!canProceedStep3}>
                                        Generate Email <i className="bx bx-envelope ms-1"></i>
                                    </Button>
                                </div>
                            </CardBody>
                        </Card>
                    )}

                    {step === 4 && (
                        <Card>
                            <CardHeader>
                                <h5 className="card-title mb-0">Generated Marketing Email</h5>
                                <div className="d-flex gap-2 mt-2">
                                    <Button
                                        color="success"
                                        size="sm"
                                        onClick={handleSend}
                                        disabled={sending || !emailColumn}
                                    >
                                        <i className="bx bx-send me-1"></i> {sending ? "Sending…" : "Send"}
                                    </Button>
                                    <Button
                                        color="primary"
                                        size="sm"
                                        onClick={() => {
                                            const blob = new Blob([generatedHtml], { type: "text/html" });
                                            const url = URL.createObjectURL(blob);
                                            const a = document.createElement("a");
                                            a.href = url;
                                            a.download = `marketing-email-${selectedDomain?.name ?? "bizserve"}.html`;
                                            a.click();
                                            URL.revokeObjectURL(url);
                                        }}
                                    >
                                        <i className="bx bx-download me-1"></i> Download HTML
                                    </Button>
                                    <Button
                                        color="info"
                                        size="sm"
                                        onClick={() => {
                                            const win = window.open("", "_blank");
                                            if (win) {
                                                win.document.write(generatedHtml);
                                                win.document.close();
                                            }
                                        }}
                                    >
                                        <i className="bx bx-show me-1"></i> Preview in New Tab
                                    </Button>
                                    <Button color="secondary" size="sm" onClick={() => setStep(1)}>
                                        Start Over
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardBody>
                                <div
                                    style={{
                                        border: "1px solid #dee2e6",
                                        borderRadius: 8,
                                        overflow: "hidden",
                                        maxHeight: 500,
                                        overflowY: "auto",
                                    }}
                                >
                                    <iframe
                                        title="Email preview"
                                        srcDoc={generatedHtml}
                                        style={{ width: "100%", minHeight: 500, border: "none" }}
                                        sandbox="allow-same-origin"
                                    />
                                </div>
                                <p className="text-muted mt-2 mb-0">
                                    Recipients: {parsedData.length} contacts from your uploaded file. Use your email tool to send this HTML. 
                                    {nameColumn ? " Map {{recipient_name}} to your Recipient Name column." : " Map {{company_name}} to your Company column for personalization."}
                                </p>
                            </CardBody>
                        </Card>
                    )}
                </Container>
            </div>
        </React.Fragment>
    );
};

export default Marketing;
